/**
 * types.ts
 *
 * Shared type definitions for unified form field architecture
 */

import { Field } from "../../../types/types";

/**
 * Form field rendering mode
 * - preview: Non-interactive form preview in form builder
 * - dashboard: Interactive form for internal users
 * - public: Interactive form for external users
 */
export type FormFieldMode = 'preview' | 'dashboard' | 'public';

/**
 * Base props that all field components receive
 */
export interface BaseFieldProps {
  /** Field configuration from the database */
  fieldConfig: Field;

  /** Current rendering mode */
  mode: FormFieldMode;

  /** Current field value */
  value: any;

  /** Callback when field value changes */
  onChange: (fieldId: string, value: any) => void;

  /** Array of validation error messages */
  errors?: string[];

  /** Whether the field should be disabled/readonly */
  disabled?: boolean;

  /** Whether to show the field in an error state */
  hasError?: boolean;

  /** Optional callback for camera modal */
  openCameraModal?: (field: Field, promptId?: string) => void;

  /** Optional callback for payment modal */
  openPaymentModal?: () => void;

  /** Payment field status: processing during verification, cancelled/failed after */
  paymentStatus?: 'processing' | 'cancelled' | 'failed';

  /**
   * The amount the server actually bound to the transaction, in MAJOR units,
   * returned by the pre-initialize webhook.
   *
   * Payment fields whose amount is decided server-side carry
   * `amountConfig: { type: 'fixed', amount: 0 }` — there is no figure to put
   * there, because it depends on answers the form has collected. Without this
   * prop the field falls back to that 0 and renders a confident, wrong
   * "R0.00". This is the real number, and it wins over amountConfig.
   */
  preloadedAmount?: number;

  /** Additional CSS classes */
  className?: string;

  /** All current form values - allows fields to reference other field values */
  formValues?: Record<string, any>;

  /** Whether this field is part of a payment-enabled form (display-only mode) */
  isPaymentForm?: boolean;
}

/**
 * Props for fields that support file upload
 */
export interface FileFieldProps extends BaseFieldProps {
  /** Array of uploaded files */
  value: File[] | string[] | null;
}

/**
 * Props for signature field
 */
export interface SignatureFieldProps extends BaseFieldProps {
  /** Signature data URL or File */
  value: string | File | null;
}

/**
 * Field component type definition
 */
export type FieldComponent = React.ComponentType<BaseFieldProps>;

/**
 * Field component registry map
 * Maps field type strings to their corresponding React components
 */
export type FieldComponentMap = {
  [key: string]: FieldComponent;
};

/**
 * Security configuration for field rendering
 */
export interface FieldSecurityConfig {
  /** Field types allowed in public mode */
  publicAllowedTypes: string[];

  /** Field types that require authentication */
  authRequiredTypes: string[];

  /** Field types that are dashboard-only */
  dashboardOnlyTypes: string[];
}

/**
 * Default security configuration
 */
export const DEFAULT_SECURITY_CONFIG: FieldSecurityConfig = {
  publicAllowedTypes: [
    'text',
    'textarea',
    'number',
    'date',
    'dropdown',
    'multiple-choice',
    'checkboxes',
    'checkbox',
    'yes-no-toggle',
    'comment-box',
    'signature-pad',
    'file-upload',
    'image_upload',
    'camera_upload',
    'multi_image_capture',
    'barcode-scanner',
    'information-box',
    'payment',
    'hidden',
    'url',
  ],
  authRequiredTypes: [],
  dashboardOnlyTypes: [],
};

/**
 * Base CSS classes for form inputs
 */
export const BASE_INPUT_CLASSES = 'w-full px-4 py-2 border rounded-md text-sm bg-white text-black';

/**
 * Error state CSS classes
 */
export const ERROR_INPUT_CLASSES = 'border-red-500';

/**
 * Disabled state CSS classes
 */
export const DISABLED_INPUT_CLASSES = 'opacity-50 cursor-not-allowed';
